import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { colors } from '@/constants/colors';
import { getShieldApi, setShieldApi, type Shield } from '@/api/vendo';

/**
 * Device Settings → Session & Network → "Website blocking" (#41, Ray
 * 2026-10-06). Two lists the operator types + one switch:
 *   - Blocklist websites (blocked for everyone, paid or not)
 *   - Whitelist websites (open even before a coin)
 *   - Block adult sites (ready-made list)
 * Router-wide: a sub-vendo shows its main vendo's lists, read-only.
 * Every add, remove and tick saves at once (optimistic; reverted if the
 * server refuses) - no Save button (Ray 2026-10-23).
 */

const WebsiteBlockingSection = ({ deviceId }: { deviceId: string }) => {
   const queryClient = useQueryClient();
   const { data, isLoading } = useQuery({
      queryKey: ['device-shield', deviceId],
      queryFn: () => getShieldApi(deviceId),
   });
   const [shield, setShield] = useState<Shield>({ blockDomains: [], freeDomains: [], blockAdult: false });
   const [blockText, setBlockText] = useState('');
   const [freeText, setFreeText] = useState('');
   const [error, setError] = useState('');
   // The last state the server accepted, and the newest one asked for, so two
   // quick adds both land and a refused save rolls back to what is saved.
   const saved = useRef<Shield>(shield);
   const latest = useRef<Shield>(shield);

   useEffect(() => {
      if (data?.shield) {
         setShield(data.shield);
         saved.current = data.shield;
         latest.current = data.shield;
      }
   }, [data]);

   const inherited = data?.inheritedFrom ?? null;

   const persist = async (next: Shield) => {
      latest.current = next;
      setShield(next);
      setError('');
      try {
         const r = await setShieldApi(deviceId, next);
         saved.current = r.shield;
         if (latest.current === next) setShield(r.shield);
         queryClient.setQueryData(['device-shield', deviceId], { ...data, shield: r.shield });
         toast.success(r.applied === 'now' ? 'Saved' : 'Saved — the vendo gets it within a minute', {
            autoClose: 2000,
         });
      } catch (err: any) {
         if (latest.current === next) {
            latest.current = saved.current;
            setShield(saved.current);
         }
         setError(err?.response?.data?.error || 'Could not save website blocking');
      }
   };

   const add = (key: 'blockDomains' | 'freeDomains', text: string, clear: () => void) => {
      const items = text
         .split(/[\s,]+/)
         .map((s) => s.trim())
         .filter(Boolean);
      if (items.length === 0) return;
      const cur = latest.current;
      clear();
      persist({ ...cur, [key]: Array.from(new Set([...cur[key], ...items])) });
   };
   const remove = (key: 'blockDomains' | 'freeDomains', d: string) => {
      const cur = latest.current;
      persist({ ...cur, [key]: cur[key].filter((x) => x !== d) });
   };

   if (isLoading) return <Hint>Loading website blocking…</Hint>;

   return (
      <Wrap>
         <Title>Website blocking</Title>
         {inherited && (
            <Hint>
               This covers the whole router, so it is set on the main vendo
               {inherited.name ? ` (${inherited.name})` : ''}. Open the main vendo to change it.
            </Hint>
         )}

         <Label htmlFor="shield-block">Blocklist websites</Label>
         <Hint>Example: facebook.com blocks Facebook (and m.facebook.com) for everyone, paid or not.</Hint>
         {!inherited && (
            <AddRow>
               <Input
                  id="shield-block"
                  value={blockText}
                  placeholder="e.g. facebook.com"
                  onChange={(e) => setBlockText(e.target.value)}
                  onKeyDown={(e) => {
                     if (e.key === 'Enter') {
                        e.preventDefault();
                        add('blockDomains', blockText, () => setBlockText(''));
                     }
                  }}
               />
               <AddBtn type="button" onClick={() => add('blockDomains', blockText, () => setBlockText(''))}>
                  <PlusIcon />
                  Add
               </AddBtn>
            </AddRow>
         )}
         <Chips aria-label="Blocked websites">
            {shield.blockDomains.length === 0 && <Empty>Nothing blocked</Empty>}
            {shield.blockDomains.map((d) => (
               <Chip key={d}>
                  {d}
                  {!inherited && (
                     <ChipX type="button" aria-label={`Unblock ${d}`} onClick={() => remove('blockDomains', d)}>
                        ×
                     </ChipX>
                  )}
               </Chip>
            ))}
         </Chips>

         <Label htmlFor="shield-free">Whitelist websites</Label>
         <Hint>Example: myshop.ph — customers can open these websites even without a session yet.</Hint>
         {!inherited && (
            <AddRow>
               <Input
                  id="shield-free"
                  value={freeText}
                  placeholder="e.g. myshop.ph"
                  onChange={(e) => setFreeText(e.target.value)}
                  onKeyDown={(e) => {
                     if (e.key === 'Enter') {
                        e.preventDefault();
                        add('freeDomains', freeText, () => setFreeText(''));
                     }
                  }}
               />
               <AddBtn type="button" onClick={() => add('freeDomains', freeText, () => setFreeText(''))}>
                  <PlusIcon />
                  Add
               </AddBtn>
            </AddRow>
         )}
         <Chips aria-label="Free websites">
            {shield.freeDomains.length === 0 && <Empty>None</Empty>}
            {shield.freeDomains.map((d) => (
               <Chip key={d} $free>
                  {d}
                  {!inherited && (
                     <ChipX type="button" aria-label={`Remove ${d}`} onClick={() => remove('freeDomains', d)}>
                        ×
                     </ChipX>
                  )}
               </Chip>
            ))}
         </Chips>

         <Toggle>
            <input
               id="shield-adult"
               type="checkbox"
               checked={shield.blockAdult}
               disabled={!!inherited}
               onChange={(e) => persist({ ...latest.current, blockAdult: e.target.checked })}
            />
            <label htmlFor="shield-adult">Block adult sites</label>
         </Toggle>
         <Hint>Uses a ready-made list of about 8,000 adult websites.</Hint>

         {error && <ErrorText role="alert">{error}</ErrorText>}
      </Wrap>
   );
};

export default WebsiteBlockingSection;

const Wrap = styled.div`
   display: flex;
   flex-direction: column;
   gap: 8px;
   margin-top: 18px;
   padding-top: 16px;
   border-top: 1px solid ${colors.GRAY13};
`;
const Title = styled.div`
   font-size: 15px;
   font-weight: 700;
   color: ${colors.GRAY1};
`;
const Label = styled.label`
   margin-top: 8px;
   font-size: 13px;
   font-weight: 600;
   color: ${colors.GRAY1};
`;
const Hint = styled.div`
   font-size: 12px;
   color: ${colors.GRAY3};
`;
const AddRow = styled.div`
   display: flex;
   gap: 8px;
`;
const Input = styled.input`
   flex: 1;
   min-width: 0;
   padding: 9px 11px;
   border: 1px solid ${colors.GRAY12};
   border-radius: 8px;
   font-size: 14px;
`;
const PlusIcon = () => (
   <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
      <path d="M6 1v10M1 6h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
   </svg>
);
const AddBtn = styled.button`
   align-self: center;
   display: inline-flex;
   align-items: center;
   gap: 4px;
   padding: 5px 10px;
   font-size: 13px;
   border: 1px solid ${colors.PRIMARY};
   border-radius: 8px;
   background: transparent;
   color: ${colors.PRIMARY};
   font-weight: 600;
   cursor: pointer;
`;
const Chips = styled.div`
   display: flex;
   flex-wrap: wrap;
   gap: 6px;
`;
const Chip = styled.span<{ $free?: boolean }>`
   display: inline-flex;
   align-items: center;
   gap: 6px;
   padding: 4px 6px 4px 10px;
   border-radius: 999px;
   font-size: 13px;
   /* translucent fills read on both the light and dark app themes */
   background: ${({ $free }) => ($free ? 'rgba(25, 135, 84, 0.14)' : 'rgba(220, 53, 69, 0.12)')};
   color: ${({ $free }) => ($free ? colors.SUCCESS : colors.DANGER)};
`;
const ChipX = styled.button`
   border: none;
   background: transparent;
   cursor: pointer;
   font-size: 15px;
   line-height: 1;
   color: inherit;
`;
const Empty = styled.span`
   font-size: 12px;
   color: ${colors.GRAY10};
`;
const Toggle = styled.div`
   display: flex;
   align-items: center;
   gap: 8px;
   margin-top: 10px;
   font-size: 14px;
   font-weight: 600;
`;
const ErrorText = styled.div`
   font-size: 13px;
   color: ${colors.DANGER};
`;
